import React, { useEffect, useState } from "react";
import clsx from "clsx";
import { CardContent, Grid } from "@mui/material";
import { Box, Card, Typography, Stack } from "@curves/ui-base";
import { WorkoutTimelineBox, WorkoutCalendarBox } from "@icoach/members/components/";
import { HealthDeclarationBox, AgreedNotesBox, SpecialBodyConditionBox, XFactorNotesBox, GuidanceNotesBox } from "@icoach/memberDetail/components";
import useMemberWorkoutApi from "@apis/useMemberWorkoutApi";
import useInBodyApi from "@apis/useInBodyApi";
import { getAddMonths, getSubMonths, parseDate, getNowTime, getFirstOfMonth, getLastOfMonth } from "@util/moment";
import useShowLoading from "@util/hook/useShowLoading";
import { useMemberDetailContext } from "@icoach/memberDetail/MemberDetailContext";
import { isFunction } from "@util/utils";

const WorkoutTimelineCard = props => {
	const { memberID } = props;
	const { getMemberWorkoutTimeLineApi } = useMemberWorkoutApi();
	const [pageInfo, setPageInfo] = useState({
		pageIndex: 1,
		pageSize: 7,
	});
	const [data, setData] = useState({});
	const { prevText, nextText } = data;

	const doMemberWorkoutTimeLineApi = async (memberID, params) => {
		let res = await getMemberWorkoutTimeLineApi(memberID, params);
		if (res) {
			setData(res);
		}
	};

	const getParams = () => {
		return {
			...pageInfo,
		};
	};

	const changePage = pageIndex => {
		let pageInfoObj = {
			...pageInfo,
			pageIndex,
		};
		setPageInfo(pageInfoObj);
	};

	const handlePrev = () => {
		let pageIndex = pageInfo.pageIndex;
		if (!prevText) return false;
		pageIndex++;
		changePage(pageIndex);
	};

	const handleNext = () => {
		let pageIndex = pageInfo.pageIndex;
		if (!nextText) return false;
		pageIndex--;
		changePage(pageIndex);
	};

	useEffect(() => {
		doMemberWorkoutTimeLineApi(memberID, getParams());
		// eslint-disable-next-line
	}, [pageInfo]);

	return (
		<Card>
			<Card.CardContent className="py-2 px-3">
				<Typography variant="h6" className="text-center ">
					WO月統計
				</Typography>
				<WorkoutTimelineBox data={data} pageSize={pageInfo.pageSize} handlePrev={handlePrev} handleNext={handleNext} />
			</Card.CardContent>
		</Card>
	);
};
const WorkoutCalendarCard = props => {
	const { memberID, dialogRef } = props;
	const showLoading = useShowLoading();
	const { getMemberWorkoutCalendarApi } = useMemberWorkoutApi();
	const { getMeasureRangeDateApi } = useInBodyApi();
	const [currentDate, setCurrentDate] = useState(getNowTime());
	const [recordTime, setRecordTime] = useState(getNowTime());
	const [sourceData, setSourceData] = useState({});

	const doMemberWorkoutCalendarApi = async (memberID, p1, p2) => {
		showLoading(true);
		let mergeData = {};
		const workoutResp = await getMemberWorkoutCalendarApi(memberID, p1);
		const measureDatesResp = await getMeasureRangeDateApi(memberID, p2);
		if (workoutResp) {
			Object.assign(mergeData, { workoutData: workoutResp });
		}
		if (measureDatesResp) {
			Object.assign(mergeData, { measureRangeDate: measureDatesResp });
		}
		setSourceData(mergeData);
		setRecordTime(getNowTime());
		showLoading(false);
	};

	const getMemberWorkoutParams = () => {
		return {
			calTime: parseDate(currentDate, "YYYYMM"),
		};
	};

	const getMeasureRangeDateParams = () => {
		return {
			startDate: getFirstOfMonth(currentDate, -1),
			endDate: getLastOfMonth(currentDate),
		};
	};

	const handlePrev = calendarToShow => {
		let calTime = getSubMonths(currentDate, calendarToShow);
		setCurrentDate(calTime);
	};

	const handleNext = calendarToShow => {
		let calTime = getAddMonths(currentDate, calendarToShow);
		setCurrentDate(calTime);
	};

	const refreshCalendar = () => {
		doMemberWorkoutCalendarApi(memberID, getMemberWorkoutParams(), getMeasureRangeDateParams());
	};

	const handleDayClick = dayData => {
		const { openCheckInDialog } = dialogRef.current || {};
		isFunction(openCheckInDialog) && openCheckInDialog({ ...dayData, memberID }, refreshCalendar);
	};

	useEffect(() => {
		doMemberWorkoutCalendarApi(memberID, getMemberWorkoutParams(), getMeasureRangeDateParams());
		// eslint-disable-next-line
	}, [currentDate]);

	return (
		<Card>
			<Card.CardContent className="px-3 pt-2 pb-5">
				<Typography variant="h6" className="text-center mb-2">
					運動紀錄
				</Typography>
				<WorkoutCalendarBox key={`${recordTime}`} currentDate={currentDate} sourceData={sourceData} handlePrev={handlePrev} handleNext={handleNext} onDayClick={handleDayClick} />
				<Stack className={'workout-calendar-tip'} justifyContent={'center'} alignItems={'center'}>
					<span className={'workout-calendar-tip-item today'}>當日</span>
					<span className={'workout-calendar-tip-item workout'}>運動日</span>
					<span className={'workout-calendar-tip-item cross-club'}>跨店運動</span>
					<span className={'workout-calendar-tip-item measure'}>量身日</span>
				</Stack>
			</Card.CardContent>
		</Card>
	);
};

const MemberDetailWorkoutContent = ({ className, memberID, sourceData: memberProfile }) => {
	const { dialogRef, updateXFactorNotes, updateAgreedNotes, updateGuidanceNotes, updateSpecialBodyCondition } = useMemberDetailContext();
	const xFactorNotes = memberProfile?.xFactorNotes || [];
	const agreedNotes = memberProfile?.agreedNotes || [];

	return (
		<Box className={clsx("sports-record-content", className)}>
			<Grid spacing={2} container>
				<Grid xs={3} item>
					<Card className="h-100">
						<CardContent>
							<Stack direction="column" alignItems="center">
								<Typography variant="h6" className="mb-2">
									WO頻率
								</Typography>
								<Typography variant="h4" component="p" className="text-primary font-weight-bold">
									{memberProfile.monthlyWorkoutFrequency}
								</Typography>
							</Stack>
						</CardContent>
					</Card>
				</Grid>
				<Grid xs={9} item>
					<WorkoutTimelineCard memberID={memberID} />
				</Grid>
				<Grid xs={12} item>
					<WorkoutCalendarCard memberID={memberID} dialogRef={dialogRef} />
				</Grid>
				<Grid xs={12} item>
					<Card>
						<CardContent>
							<Typography variant="h6" className="mb-2">
								X-Factor 與約定
							</Typography>
							<Grid spacing={2} container>
								<Grid xs={6} item>
									<XFactorNotesBox
										xFactorNotes={xFactorNotes}
										onEdit={() =>
											dialogRef.current &&
											dialogRef.current?.openXFactorDialog(
												{
													xFactorNotes,
													memberID,
												},
												updateXFactorNotes,
											)
										}
									/>
								</Grid>
								<Grid xs={6} item>
									<AgreedNotesBox
										agreedNotes={agreedNotes}
										onEdit={() =>
											dialogRef.current &&
											dialogRef.current.openAgreedNotesDialog(
												{
													agreedNotes,
													memberID,
												},
												updateAgreedNotes,
											)
										}
									/>
								</Grid>
							</Grid>
						</CardContent>
					</Card>
				</Grid>
				<Grid xs={12} item>
					<Card>
						<CardContent>
							<Typography variant="h6" className="mb-2">
								健康與指導
							</Typography>
							<GuidanceNotesBox
								guidanceNotes={memberProfile?.guidanceNotes}
								onEdit={() =>
									dialogRef.current &&
									dialogRef.current.openGuidanceNotesDialog(
										{
											guidanceNotes: memberProfile.guidanceNotes,
											memberID,
										},
										updateGuidanceNotes,
									)
								}
								className="mb-2"
							/>
							<SpecialBodyConditionBox
								specialBodyCondition={memberProfile?.specialBodyCondition}
								onEdit={() => {
									dialogRef.current &&
										dialogRef.current.openSpecialBodyConditionDialog(
											{
												specialBodyCondition: memberProfile.specialBodyCondition,
												memberID,
											},
											updateSpecialBodyCondition,
										);
								}}
								className={"mb-2"}
							/>
							<HealthDeclarationBox memberID={memberID} memberShipStatus={memberProfile?.memberShipStatus} healthDeclaration={memberProfile?.healthDeclaration} />
						</CardContent>
					</Card>
				</Grid>
			</Grid>
		</Box>
	);
};

export default MemberDetailWorkoutContent;
